import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import { closed } from "../../components/drawer/drawerSlice";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  createMeals,
  getAllMeals,
  getMealsByID,
  updateMeals,
} from "./services/meals.services";
import { clearMealsDetails } from "./mealsSlice";
import FormTextArea from "../../components/formFields/FormTextArea";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { IMealCategory } from "../mealcategory/mealCategoryModel";
import { getAllActiveMealCategories } from "../mealcategory/services/mealCategory.services";

const CreateMeals: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const MealsDetail = useAppSelector((state) => state.meals.MealsDetail);
  const MealCategoryList = useAppSelector(
    (state) => state.mealCategory.MealCategoryList
  );
  const MealsID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.meals.formLoading);

  useEffect(() => {
    if (MealsID) {
      dispatch(getMealsByID(MealsID));
    }
  }, [MealsID]);

  useEffect(() => {
    dispatch(getAllActiveMealCategories());
    return () => {
      dispatch(clearMealsDetails());
      dispatch(closed());
    };
  }, []);

  const handleSubmit = async (values: any) => {
    try {
      if (!MealsID) {
        const payload = {
          MealName: values.MealName ? values.MealName : "",
          Description: values.Description ? values.Description : "",
          MealCategoryID: values.MealCategoryID ? values.MealCategoryID : null,
          Rate: values.Rate ? +values.Rate : null,
          IsActive: values.IsActive ? values.IsActive : true,
        };
        const response = await dispatch(createMeals(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearMealsDetails());
          dispatch(getAllMeals());
        }
      } else {
        const payload = {
          ID: MealsID,
          MealName: values.MealName ? values.MealName : "",
          Description: values.Description ? values.Description : "",
          MealCategoryID: values.MealCategoryID ? values.MealCategoryID : null,
          Rate: values.Rate ? +values.Rate : null,
          IsActive: values.IsActive ? values.IsActive : false,
        };
        const response = await dispatch(updateMeals(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearMealsDetails());
          dispatch(getAllMeals());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={MealsDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="MealName"
                label="Meal"
                component={FormTextField}
                validator={requiredValidator}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="MealCategoryID"
                label="Meal Category"
                component={FormSelectionField}
                options={MealCategoryList?.map(
                  (mealcategory: IMealCategory) => {
                    return {
                      value: mealcategory?.ID,
                      label: mealcategory?.Title,
                    };
                  }
                )}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="Rate"
                label="Rate"
                component={FormTextField}
                validator={requiredValidator}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="Description"
                label="Description"
                component={FormTextArea}
              />
            </GridLayoutItem>
            <GridLayoutItem
              colSpan={3}
              style={{
                textAlign: "end",
                marginTop: "10px",
                width: "100%",
              }}
            >
              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginRight: 4 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
              <ButtonWithLoading
                label={MealsID ? "Update" : "Create"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />
            </GridLayoutItem>
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreateMeals;
